"use client";

import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheckBig,
  Calendar,
  TriangleAlert,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { CitationMarkdown } from "@/components/notebook/CitationMarkdown";
import { SourceDisplay } from "@/components/notebook/SourceDisplay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedditAlien, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { Badge } from "@/components/ui/badge";
import { useArtifact } from "@/hooks/use-artifact";
import { getViewModeChrome } from "../view-mode-chrome";

interface UnifiedSearchChatSuccessProps {
  result: {
    content?: string;
    sources?: Array<{
      text: string;
      author: string;
      score: number | null;
      url: string;
      post_title?: string;
      created_at: string;
      source_type: string;
      [key: string]: any;
    }>;
    purpose?: string;
    // Canceled state fields
    status?: string;
    error_type?: string;
    error_message?: string;
    iteration?: number;
    stage?: string;
    timestamp?: number;
    id?: string;
  };
  viewMode?: string;
}

export function UnifiedSearchChatSuccess({
  result,
  viewMode,
}: UnifiedSearchChatSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  const { artifact } = useArtifact();
  const [showResults, setShowResults] = useState(true); // true = open, false = closed
  const [showSources, setShowSources] = useState(false);
  const [highlightedSourceIndex, setHighlightedSourceIndex] = useState<
    number | null
  >(null);
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([
    "reddit",
    "twitter",
    "news",
  ]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const sourcesContainerRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  // Safe property access with null checks  
  const resultStatus = result?.status;
  const resultPurpose = result?.purpose;
  const resultContent = result?.content;
  const resultSources = result?.sources;
  
  // Check if this is a canceled state
  const isCanceled = resultStatus === "canceled";
  
  // Parse purpose into query and prompt_type (only for successful results)
  let title: string;
  let subtitle: string;
  
  if (isCanceled) {
    title = "Search Canceled";
    subtitle = "Stopped by user";
  } else if (resultPurpose) {
    const [promptType, query] = resultPurpose.split(" | ");
    title = query || resultPurpose;
    subtitle = promptType || "Search results";
  } else {
    title = "Search Results";
    subtitle = "No results available";
  }

  // Handle citation click
  const handleCitationClick = (citationIndex: number) => {
    // Expand sources section
    setShowSources(true);

    // Reset filters to show all sources
    setSelectedFilters(["reddit", "twitter", "news"]);

    // Set highlighted source
    setHighlightedSourceIndex(citationIndex - 1);

    // Scroll to source after DOM update with longer delay
    setTimeout(() => {
      const sourceElement = sourcesContainerRef.current?.querySelector(
        `[data-source-index="${citationIndex - 1}"]`
      );
      if (sourceElement) {
        sourceElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);

    // Remove highlight after 1 second
    setTimeout(() => {
      setHighlightedSourceIndex(null);
    }, 2000);
  };

  // Display full content since we removed half state
  const displayContent = resultContent || (isCanceled ? "Search was canceled by user." : "No content available.");

  // Get source counts by type
  const getSourceCounts = () => {
    const counts: Record<string, number> = { reddit: 0, twitter: 0, news: 0 };
    if (resultSources) {
      resultSources.forEach((source) => {
        const type = source.source_type.toLowerCase();
        if (type in counts) counts[type]++;
      });
    }
    return counts;
  };

  // Add originalIndex to sources and filter/sort
  const sourcesWithIndex = resultSources ? resultSources.map((source, index) => ({
    ...source,
    originalIndex: index,
  })) : [];

  const filteredAndSortedSources = sourcesWithIndex
    .filter((source) =>
      selectedFilters.includes(source.source_type.toLowerCase())
    )
    .sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

  // Toggle filter
  const toggleFilter = (sourceType: string) => {
    setSelectedFilters((prev) =>
      prev.includes(sourceType)
        ? prev.filter((f) => f !== sourceType)
        : [...prev, sourceType]
    );
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            {/* Search icon on the left */}
            <Search
                className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                strokeWidth={2}
              />

            {/* Text content */}
            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {title}
              </span>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                {subtitle}
              </span>
            </div>
          </div>

          {/* Status icon on the right */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            {isCanceled ? (
              <TriangleAlert className="h-4 w-4 text-yellow-500" />
            ) : (
              <CircleCheckBig className="h-4 w-4 text-green-500" />
            )}
          </motion.div>
        </div>

        <div className="border-t border-border">
          {/* Search Results Section - only show if not canceled */}
          {!isCanceled && (
          <div className="border-b border-border">
            <div
              onClick={() => setShowResults(!showResults)}
              role="button"
              tabIndex={0}
              className="w-full px-3 py-2 flex items-center gap-2 hover:bg-muted/50 transition-all duration-200 text-xs font-medium text-foreground/90 cursor-pointer select-none"
            >
              {showResults ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              Search Results
            </div>
            <AnimatePresence>
              {showResults && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "auto" }}
                  exit={{ height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-3 pb-3 pt-1">
                    <div className="prose prose-sm max-w-none prose-invert">
                      {isCanceled ? (
                        <p className="text-muted-foreground">{displayContent}</p>
                      ) : (
                        <CitationMarkdown onCitationClick={handleCitationClick}>
                          {displayContent}
                        </CitationMarkdown>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          )}

          {/* Sources Section */}
          {resultSources && resultSources.length > 0 && (
            <div>
              <div
                onClick={() => setShowSources(!showSources)}
                role="button"
                tabIndex={0}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted/50 transition-all duration-200 text-xs font-medium text-foreground/90 cursor-pointer select-none"
              >
                <div className="flex items-center gap-2">
                  {showSources ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                  <div className="flex items-center gap-1">
                    <span>Sources ({resultSources?.length || 0})</span>
                    <div className="flex items-center -space-x-1">
                      <FontAwesomeIcon icon={faXTwitter} className="w-2 h-2 text-foreground" />
                      <FontAwesomeIcon
                          icon={faRedditAlien}
                          className="w-2 h-2 text-white"
                        />
                      <div className="w-3 h-3 rounded-full bg-foreground flex items-center justify-center">
                        <svg
                          className="w-2 h-2 text-background"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 5v14H5V5h14zm0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                          <path d="M14 17H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sort and Filter Controls - Only show when sources are open */}
                <AnimatePresence>
                  {showSources && (
                    <motion.div
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center gap-2"
                    >
                      {/* Date Sort Button */}
                      <div onClick={(e) => e.stopPropagation()}>
                      <Badge
                        variant="outline"
                        onClick={() => {
                          setSortOrder(
                            sortOrder === "newest" ? "oldest" : "newest"
                          );
                        }}
                        className="text-[10px] cursor-pointer transition-colors border border-border px-1.5 py-0.5 h-6 min-w-[40px] text-foreground bg-card hover:bg-muted flex items-center justify-center"
                        title={`Sort by ${
                          sortOrder === "newest" ? "oldest" : "newest"
                        } first`}
                      >
                        <Calendar className="w-3 h-3 mr-0.5" />
                        <ChevronDown
                          className={`w-3 h-3 transition-transform ${
                            sortOrder === "newest" ? "rotate-0" : "rotate-180"
                          }`}
                        />
                      </Badge>
                      </div>

                      {/* Filter Dropdown */}
                      <div className="relative" ref={filterRef} onClick={(e) => e.stopPropagation()}>
                        <Badge
                          variant="outline"
                          onClick={() => setIsFilterOpen(!isFilterOpen)}
                          className="text-[10px] cursor-pointer transition-colors border border-border px-1.5 py-0.5 h-6 min-w-[40px] text-foreground bg-card hover:bg-muted flex items-center justify-center"
                          title="Filter by source type"
                        >
                          <ChevronDown className="w-3 h-3 mr-0.5" />
                          {selectedFilters.length <= 3 && (
                            <span>{selectedFilters.length}</span>
                          )}
                        </Badge>

                        {isFilterOpen && (
                          <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-md shadow-lg z-10 min-w-[120px]">
                            {Object.entries(getSourceCounts()).map(
                              ([sourceType, count]) => (
                                <label
                                  key={sourceType}
                                  className="filter-dropdown-item flex items-center gap-2 px-3 py-2 text-[11px] text-foreground cursor-pointer hover:bg-muted"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedFilters.includes(
                                      sourceType
                                    )}
                                    onChange={() => toggleFilter(sourceType)}
                                    className="w-3 h-3 rounded"
                                  />
                                  <span className="capitalize">
                                    {sourceType}
                                  </span>
                                  <span className="text-muted-foreground">
                                    ({count})
                                  </span>
                                </label>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence>
                {showSources && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 pt-1">
                      {/* Sources Container */}
                      <div
                        ref={sourcesContainerRef}
                        className="max-h-96 overflow-y-auto"
                      >
                        <SourceDisplay
                          sources={filteredAndSortedSources}
                          highlightedSourceIndex={highlightedSourceIndex}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
